import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/app/StatusBadge";
import { paymentMethodLabel } from "@/lib/orders";
import { toast } from "sonner";
import { CheckCircle2, Filter, RotateCcw, Wallet, Trash2 } from "lucide-react";

const EMPTY = { status: "all", partnerId: "all", method: "all", date_from: "", date_to: "" };

export default function AdminRecharges() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (applied.status !== "all") params.status = applied.status;
    if (applied.partnerId !== "all") params.partner_id = applied.partnerId;
    if (applied.method !== "all") params.payment_method = applied.method;
    if (applied.date_from) params.date_from = applied.date_from;
    if (applied.date_to) params.date_to = applied.date_to;
    const { data } = await api.get("/recharges", { params });
    setRows(data);
  }, [applied]);
  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/recharges/stats"); setStats(data); } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.post("/recharges/mark-all-seen").catch(() => {}).finally(() => window.dispatchEvent(new Event("badges:refresh")));
  }, []);
  useEffect(() => { loadStats(); api.get("/partners").then(r => setPartners(r.data)).catch(() => {}); }, [loadStats]);

  const confirmBank = async (r) => {
    setConfirming(r.id);
    try {
      await api.post(`/recharges/${r.id}/bank-transfer/confirm`);
      toast.success(`Bonifico confermato · credito accreditato`);
      await load(); await loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore conferma"); }
    finally { setConfirming(null); }
  };

  const remove = async (r) => {
    const msg = r.payment_status === "pagato"
      ? `Eliminare la ricarica ${r.order_number}?\nVerranno stornati ${eur(r.credit_amount)} dal credito del partner${r.promo_code ? ` e liberato l'utilizzo del codice ${r.promo_code}` : ""}.`
      : `Eliminare la ricarica ${r.order_number}?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/recharges/${r.id}`);
      toast.success(data.reversed_credit > 0 ? `Ricarica eliminata · stornati ${eur(data.reversed_credit)}` : "Ricarica eliminata");
      await load(); await loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };

  const totalFiltered = useMemo(() => rows.reduce((s, r) => s + Number(r.total || 0), 0), [rows]);

  return (
    <div className="space-y-6" data-testid="admin-recharges">
      <div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Ricariche credito</h1>
        <p className="text-slate-500 mt-1">Storico, verifica bonifici e statistiche delle ricariche partner.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card className="border-emerald-200 bg-emerald-50/40"><CardContent className="p-4">
            <div className="text-xs uppercase text-emerald-700 font-bold tracking-widest">Credito attivo</div>
            <div className="font-display text-2xl font-extrabold text-emerald-700" data-testid="stats-active-credit">{eur(stats.active_credit_total)}</div>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500 font-bold tracking-widest">Ricariche vendute</div>
            <div className="font-display text-2xl font-extrabold" data-testid="stats-paid-total">{eur(stats.recharges_paid_total)}</div>
            <div className="text-xs text-slate-500">{stats.recharges_paid_count} completate</div>
          </CardContent></Card>
          <Card className="border-amber-200 bg-amber-50/40"><CardContent className="p-4">
            <div className="text-xs uppercase text-amber-700 font-bold tracking-widest">Bonus erogati</div>
            <div className="font-display text-2xl font-extrabold text-amber-700" data-testid="stats-bonus-total">{eur(stats.recharges_bonus_total)}</div>
          </CardContent></Card>
          <Card className="border-indigo-200 bg-indigo-50/40"><CardContent className="p-4">
            <div className="text-xs uppercase text-indigo-700 font-bold tracking-widest">Credito consegnato</div>
            <div className="font-display text-2xl font-extrabold text-indigo-700">{eur(stats.recharges_credit_delivered)}</div>
          </CardContent></Card>
          <Card className="border-red-200 bg-red-50/40"><CardContent className="p-4">
            <div className="text-xs uppercase text-red-700 font-bold tracking-widest">In attesa bonifico</div>
            <div className="font-display text-2xl font-extrabold text-red-700" data-testid="stats-pending">{stats.pending_recharges_count}</div>
          </CardContent></Card>
        </div>
      )}

      {stats && stats.pending_bank && stats.pending_bank.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="pending-bonifico-list">
          <CardContent className="p-5">
            <h2 className="font-display font-bold text-lg mb-3">Bonifici da verificare</h2>
            <div className="divide-y">
              {stats.pending_bank.map((r) => (
                <div key={r.id} className="py-2 flex items-center gap-3 flex-wrap" data-testid={`pending-bank-${r.order_number}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{r.order_number} · {r.package_name}</div>
                    <div className="text-xs text-slate-500">{r.partner_name} · {dateIt(r.created_at)} · {eur(r.total)}</div>
                  </div>
                  <Button size="sm" onClick={() => confirmBank(r)} disabled={confirming === r.id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`confirm-bank-${r.order_number}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> {confirming === r.id ? "..." : "Conferma bonifico"}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200"><CardContent className="p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[200px]">
          <Label className="text-xs">Partner</Label>
          <Select value={filters.partnerId} onValueChange={(v) => setFilters({ ...filters, partnerId: v })}>
            <SelectTrigger data-testid="recharges-partner-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i partner</SelectItem>
              {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs">Stato pagamento</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="recharges-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="in_attesa">In attesa</SelectItem>
              <SelectItem value="pagato">Pagato</SelectItem>
              <SelectItem value="annullato">Annullato</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs">Metodo</Label>
          <Select value={filters.method} onValueChange={(v) => setFilters({ ...filters, method: v })}>
            <SelectTrigger data-testid="recharges-method-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="paypal">PayPal</SelectItem>
              <SelectItem value="bonifico">Bonifico</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[150px]"><Label className="text-xs">Data da</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="recharges-date-from" /></div>
        <div className="min-w-[150px]"><Label className="text-xs">Data a</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="recharges-date-to" /></div>
        <Button onClick={() => setApplied({ ...filters })} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="recharges-filter-apply"><Filter className="w-4 h-4 mr-1" /> Filtra</Button>
        <Button onClick={() => { setFilters(EMPTY); setApplied(EMPTY); }} variant="outline" data-testid="recharges-filter-reset"><RotateCcw className="w-4 h-4 mr-1" /> Azzera filtri</Button>
      </CardContent></Card>

      <Card className="border-emerald-200 bg-emerald-50/40"><CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-slate-600"><span className="font-semibold text-slate-900">{rows.length}</span> ricariche visualizzate</div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Totale pagato</div>
          <div className="font-display font-extrabold text-2xl text-emerald-700" data-testid="recharges-total-value">{eur(totalFiltered)}</div>
        </div>
      </CardContent></Card>

      <Card className="border-slate-200"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Data</TableHead><TableHead>Numero</TableHead><TableHead>Partner</TableHead>
            <TableHead>Pacchetto</TableHead><TableHead>Imponibile</TableHead><TableHead>IVA</TableHead>
            <TableHead>Totale</TableHead><TableHead>Credito</TableHead>
            <TableHead>Metodo</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`recharge-row-${r.order_number}`}>
                <TableCell>{dateIt(r.created_at)}</TableCell>
                <TableCell className="font-semibold">{r.order_number}</TableCell>
                <TableCell>{r.partner_name}</TableCell>
                <TableCell>{r.package_name}
                  {r.promo_code && <div className="text-[11px] font-mono text-amber-700" data-testid={`recharge-promo-${r.order_number}`}>{r.promo_code} · -{eur(r.discount_amount)}</div>}
                </TableCell>
                <TableCell>{eur(r.net_amount)}</TableCell>
                <TableCell>{r.vat_rate}% · {eur(r.vat_amount)}</TableCell>
                <TableCell className="font-semibold">{eur(r.total)}</TableCell>
                <TableCell className="text-emerald-700 font-semibold">{eur(r.credit_amount)}</TableCell>
                <TableCell className="text-xs">{paymentMethodLabel(r.payment_method)}</TableCell>
                <TableCell><StatusBadge status={r.payment_status} /></TableCell>
                <TableCell>
                  {r.payment_status === "in_attesa" && r.payment_method === "bonifico" && (
                    <Button size="sm" onClick={() => confirmBank(r)} disabled={confirming === r.id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`row-confirm-bank-${r.order_number}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Conferma
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => remove(r)} data-testid={`delete-recharge-${r.order_number}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-slate-500 py-10">Nessuna ricarica.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
